import { useEffect } from 'react';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import FeatureLockedPage from '@/components/common/FeatureLockedPage';
import CourseLockedPage from '@/components/common/CourseLockedPage';
import NotFound from '@/components/utils/NotFound';
import ShortcodeResolver from '@/components/ShortcodeResolver';
import EmailOptOutPage from '@/components/email/EmailOptOutPage';
import CheckoutSuccessPage from '@/components/checkout/CheckoutSuccessPage';
import CheckoutCancelPage from '@/components/checkout/CheckoutCancelPage';
import CheckoutLauncher from '@/components/checkout/CheckoutLauncher';
import RegisterCheckoutPage from '@/components/checkout/RegisterCheckoutPage';
import PersistentLayout from '@/components/layout/PersistentLayout';
import DayTradingChatStandalonePage from '@/components/daytrading/DayTradingChatStandalonePage';
import DayTradingLivestreamStandalonePage from '@/components/daytrading/DayTradingLivestreamStandalonePage';
import { DAY_TRADING_ROUTES } from '@/constants/routes';
import UserRoutes from '@/components/layout/UserRoutes';
import AdminRoutes from '@/components/layout/AdminRoutes';
import FrauMeetsBoersePage from '@/components/frauMeetsBoerse/FrauMeetsBoersePage';
import { FmbSubRoutes } from '@/components/frauMeetsBoerse/FmbHostRouter';
import { FRAU_MEETS_BOERSE_CONFIG } from '@/consts/frauMeetsBoerse';
import ImpressumPage from '@/components/legal/ImpressumPage';
import DatenschutzPage from '@/components/legal/DatenschutzPage';
import RisikoDisclaimerPage from '@/components/legal/RisikoDisclaimerPage';
import AGBPage from '@/components/legal/AGBPage';
import { analytics } from '@/services/analytics';
import type {FeatureConfigKey} from "@/models";

/**
 * Component that tracks page views on route changes
 */
function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    analytics.pageView(location.pathname, {
      search: location.search,
      hash: location.hash,
    });
  }, [location]);

  return null;
}

interface AppRouterProps {
  onLogout: () => void;
}

/**
 * Main application router
 * Orchestrates special routes (shortcode, feature-locked) and delegates
 * to UserRoutes and AdminRoutes for domain-specific routing
 */
export default function AppRouter({ onLogout }: AppRouterProps) {
  return (
    <>
      <PageViewTracker />
      <Routes>
      {/* Shortcode resolver route - without layout */}
      <Route
        path="/s/:code/:checkdigits"
        element={<ShortcodeResolver />}
      />

      {/* Email opt-out page - public route without layout */}
      <Route
        path="/optout/:userid/:type/:key"
        element={<EmailOptOutPage />}
      />

      {/* Checkout launcher - starts Stripe checkout for a preselected plan (e.g. marketing links) */}
      <Route
        path="/checkout"
        element={<CheckoutLauncher />}
      />

      {/* Register links for already-logged-in users - mirror register page, offer checkout instead of 404 */}
      <Route
        path="/auth/register"
        element={<RegisterCheckoutPage />}
      />

      {/* Auth pages for already-logged-in users - redirect to dashboard instead of 404 */}
      <Route
        path="/login"
        element={<Navigate to="/dashboard" replace />}
      />

      <Route
        path="/forgot-pw"
        element={<Navigate to="/dashboard" replace />}
      />

      {/* Checkout result pages - without layout */}
      <Route
        path="/checkout/success"
        element={<CheckoutSuccessPage />}
      />

      <Route
        path="/checkout/cancel"
        element={<CheckoutCancelPage />}
      />

      {/* Feature locked page - has its own layout */}
      <Route
        path="/feature-locked/:featureName"
        element={<FeatureLockedPageWrapper onLogout={onLogout} />}
      />

      {/* Course locked page - has its own layout */}
      <Route
        path="/feature-locked/course/:courseId"
        element={<CourseLockedPage onLogout={onLogout} />}
      />

      {/* Frau meets Börse Landingpage - public route without layout */}
      <Route
        path={FRAU_MEETS_BOERSE_CONFIG.routePath}
        element={<FrauMeetsBoersePage />}
      />
      {FmbSubRoutes()}

      {/* Legal pages - public routes with their own layout */}
      <Route
        path="/impressum"
        element={<ImpressumPage />}
      />

      <Route
        path="/datenschutz"
        element={<DatenschutzPage />}
      />

      <Route
        path="/risikodisclaimer"
        element={<RisikoDisclaimerPage />}
      />

      <Route
        path="/agb"
        element={<AGBPage />}
      />

      {/* Day Trading standalone chat - without sidebar/layout */}
      <Route
        path={DAY_TRADING_ROUTES.CHAT_PATTERN}
        element={<DayTradingChatStandalonePage />}
      />

      {/* Day Trading standalone livestream pop-out - without sidebar/layout */}
      <Route
        path={DAY_TRADING_ROUTES.LIVESTREAM_PATTERN}
        element={<DayTradingLivestreamStandalonePage />}
      />

      {/* All other routes with persistent layout */}
      <Route element={<PersistentLayout onLogout={onLogout} />}>
        {/* User-facing routes */}
        {UserRoutes()}

        {/* Admin-only routes */}
        {AdminRoutes()}

        {/* 404 Not Found */}
        <Route
          path="*"
          element={<NotFound onLogout={onLogout} />}
        />
      </Route>
      </Routes>
    </>
  );
}

// Wrapper component to extract route params for FeatureLockedPage
function FeatureLockedPageWrapper({ onLogout }: { onLogout: () => void }) {
  const { featureName } = useParams<{ featureName: FeatureConfigKey }>();
  return <FeatureLockedPage featureName={featureName ?? null} onLogout={onLogout} />;
}
